import type {AiContentVerificationFailure} from '@Libraries/ai-content-verification.types';
import {AiContentVerificationErrorCode} from '@Libraries/ai-content-verification.types';
import type {AiContentVerifyProps, AiContentVerifyUploadLimits} from './ai-content-verify.types';
import {
  ACCEPTED_FILE_EXTENSIONS,
  BYTES_PER_MEGABYTE,
  FAILURE_MESSAGE_KEYS,
  GENERIC_FAILURE_MESSAGE_KEY,
  HTML_FILE_EXTENSIONS,
  LOCALE_SEPARATOR_FOR_INTL,
  LOCALE_SEPARATOR_FROM_I18NEXT,
  MEGABYTE_UNIT,
} from './ai-content-verify.types';

export function getAiContentVerifyProps(): AiContentVerifyProps {
  return window.reactPageProps as AiContentVerifyProps;
}

export function getUploadLimits({maxMediaUploadBytes, maxHtmlUploadBytes}: AiContentVerifyProps): AiContentVerifyUploadLimits {
  return {mediaLimit: formatMegabytes(maxMediaUploadBytes), htmlLimit: formatMegabytes(maxHtmlUploadBytes)};
}

/**
 * Mirrors the server's checks so an obviously wrong file fails instantly instead of after a full upload; the server stays the authority.
 */
export function getClientSideRejection(file: File, props: AiContentVerifyProps): AiContentVerificationFailure | null {
  const extension = getFileExtension(file);

  if (!ACCEPTED_FILE_EXTENSIONS.includes(extension)) {
    return {code: AiContentVerificationErrorCode.UNSUPPORTED_FILE, message: ''};
  }

  if (file.size > getMaxUploadBytes(extension, props)) {
    return {code: AiContentVerificationErrorCode.UPLOAD_TOO_LARGE, message: ''};
  }

  return null;
}

export function getFailureMessage(failure: AiContentVerificationFailure, limits: AiContentVerifyUploadLimits): string {
  if (!failure.code) {
    return window.i18next.t(GENERIC_FAILURE_MESSAGE_KEY);
  }

  return window.i18next.t(FAILURE_MESSAGE_KEYS[failure.code], {...limits});
}

export function getIntlLocale(): string {
  return window.i18next.language.replace(LOCALE_SEPARATOR_FROM_I18NEXT, LOCALE_SEPARATOR_FOR_INTL);
}

function getMaxUploadBytes(extension: string, {maxMediaUploadBytes, maxHtmlUploadBytes}: AiContentVerifyProps): number {
  if (HTML_FILE_EXTENSIONS.includes(extension)) {
    return maxHtmlUploadBytes;
  }

  return maxMediaUploadBytes;
}

function getFileExtension(file: File): string {
  return file.name.split('.').pop()?.toLowerCase() ?? '';
}

function formatMegabytes(bytes: number): string {
  return new Intl.NumberFormat(getIntlLocale(), {style: 'unit', unit: MEGABYTE_UNIT, maximumFractionDigits: 0}).format(bytes / BYTES_PER_MEGABYTE);
}
